import React from "react"
import { API_BASE } from "helpers/apiClient"
import { formatDisplayNumber } from "helpers/indianNumber"
import { formatIndianDateTime } from "helpers/indianDate"

export interface ApiListing {
  _id: string
  propertyTitle?: string
  address?: string
  city?: string
  state?: string
  category?: string
  propertyImagesUrls?: string[]
  totalSalePrice?: number
  superAreaSqFt?: number
  categorySpecific?: {
    superArea?: string | number
    admin_ribbonText?: string
    numberOfBedrooms?: number
    numberOfBathrooms?: number
  }
  auctionStatus?: "live" | "upcoming" | "ended"
  isUpcoming?: boolean
  auctionStartDate?: string
  auctionEndDate?: string
  biddingStartPrice?: number
  biddingMinIncrement?: number
  currentHighestBid?: number
  reservePrice?: number
}

const formatPrice = (n: number | undefined) => {
  if (n == null || !Number.isFinite(n)) return "—"
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} Lakh`
  return `₹${n.toLocaleString("en-IN")}`
}

const formatAuctionDateTime = (iso: string | undefined) => {
  if (!iso) return null
  const formatted = formatIndianDateTime(iso, "")
  return formatted || null
}

const getCountdown = (endDate: string | undefined) => {
  if (!endDate) return null
  const end = new Date(endDate).getTime()
  const now = Date.now()
  const diff = end - now
  if (diff <= 0) return { days: 0, hours: 0, mins: 0 }
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  return { days, hours, mins }
}

const getArea = (l: ApiListing) => {
  const v = l.categorySpecific?.superArea ?? l.superAreaSqFt
  return v != null && v !== "" ? `${formatDisplayNumber(v)} sq.ft` : "—"
}

const getStatusLabel = (l: ApiListing) => {
  if (l.auctionStatus === "live") return "Live"
  if (l.isUpcoming) return "Upcoming"
  if (l.auctionStatus === "upcoming") return "Upcoming"
  if (l.auctionStatus === "ended") return "Ended"
  return "—"
}

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1554435493-93422e8220c8?w=400&h=300&fit=crop"

export interface AuctionCardProps {
  listing: ApiListing
  onViewDetails: (listing: ApiListing) => void
  className?: string
}

const AuctionCard: React.FC<AuctionCardProps> = ({ listing, onViewDetails, className = "" }) => {
  const imgUrl = listing.propertyImagesUrls?.[0]
  const countdown = getCountdown(listing.auctionEndDate)
  const startCountdown = getCountdown(listing.auctionStartDate)
  const endTimeStr = formatAuctionDateTime(listing.auctionEndDate)
  const currentHighestBid = listing.currentHighestBid ?? listing.biddingStartPrice ?? 0
  const bidStartPrice = listing.biddingStartPrice ?? 0
  const statusLabel = getStatusLabel(listing)
  const isLive = listing.auctionStatus === "live"
  const isUpcoming = listing.isUpcoming || listing.auctionStatus === "upcoming"

  const handleClick = () => onViewDetails(listing)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onViewDetails(listing)
    }
  }

  return (
    <div
      className={`auction-card ${className}`.trim()}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{ cursor: "pointer" }}
    >
      <div className="auction-card-header">
        <div className="auction-card-title">
          <h3>{listing.propertyTitle || "Property"}</h3>
        </div>
        <div className="auction-card-meta">
          <span className="meta-area">{getArea(listing)} {listing.category || ""}</span>
          <span className="meta-location">{listing.city || "—"}, {listing.state || "—"}</span>
        </div>
      </div>

      <div className="auction-card-image">
        <img
          src={imgUrl ? `${API_BASE}${imgUrl}` : DEFAULT_IMAGE}
          alt={listing.propertyTitle || "Property"}
          loading="lazy"
        />
        <button
          className="auction-favorite-btn"
          type="button"
          onClick={(e) => e.stopPropagation()}
        >
          <i className="mdi mdi-heart-outline" />
        </button>
        {listing.categorySpecific?.admin_ribbonText && (
          <div className="auction-ribbon">
            {listing.categorySpecific.admin_ribbonText}
          </div>
        )}
        <div className="auction-status-banner">
          {isLive && (
            <>
              <span className="status-dot live" />
              AUCTION IN PROGRESS | ENDS {formatAuctionDateTime(listing.auctionEndDate)}
            </>
          )}
          {isUpcoming && (
            <>
              <span className="status-dot upcoming" />
              UPCOMING | STARTS {formatAuctionDateTime(listing.auctionStartDate)}
            </>
          )}
          {statusLabel === "Ended" && (
            <>
              <span className="status-dot ended" />
              AUCTION ENDED
            </>
          )}
        </div>
      </div>

      <div className="auction-card-body">
        <div className="auction-info-row">
          {isLive && countdown && (
            <div className="auction-countdown auction-countdown--live">
              <span className="countdown-label">
                <i className="mdi mdi-timer-sand me-1" />
                Ends in
              </span>
              <div className="countdown-boxes">
                <div className="countdown-box">
                  <span className="countdown-num">{String(countdown.days).padStart(2, "0")}</span>
                  <span className="countdown-unit">D</span>
                </div>
                <div className="countdown-box">
                  <span className="countdown-num">{String(countdown.hours).padStart(2, "0")}</span>
                  <span className="countdown-unit">H</span>
                </div>
                <div className="countdown-box">
                  <span className="countdown-num">{String(countdown.mins).padStart(2, "0")}</span>
                  <span className="countdown-unit">M</span>
                </div>
              </div>
            </div>
          )}
          {isUpcoming && startCountdown && (
            <div className="auction-countdown auction-countdown--upcoming">
              <span className="countdown-label">
                <i className="mdi mdi-clock-start me-1" />
                Starts in
              </span>
              <div className="countdown-boxes">
                <div className="countdown-box">
                  <span className="countdown-num">{String(startCountdown.days).padStart(2, "0")}</span>
                  <span className="countdown-unit">D</span>
                </div>
                <div className="countdown-box">
                  <span className="countdown-num">{String(startCountdown.hours).padStart(2, "0")}</span>
                  <span className="countdown-unit">H</span>
                </div>
                <div className="countdown-box">
                  <span className="countdown-num">{String(startCountdown.mins).padStart(2, "0")}</span>
                  <span className="countdown-unit">M</span>
                </div>
              </div>
            </div>
          )}
          {isUpcoming && !startCountdown && (
            <div className="auction-countdown upcoming">
              <i className="mdi mdi-clock-outline me-1" />
              Starting Soon
            </div>
          )}
          {statusLabel === "Ended" && (
            <div className="auction-countdown ended">
              <i className="mdi mdi-flag-checkered me-1" />
              <span>Auction Closed</span>
              {endTimeStr && (
                <span className="auction-end-time d-block small mt-1 opacity-90">
                  Ended {endTimeStr}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="auction-bid-row">
          <div className="bid-info">
            <span className="bid-amount">{formatPrice(bidStartPrice)}</span>
            <span className="bid-label">Bid Start Price</span>
          </div>
          <div className="bid-info">
            <span className="bid-amount">{formatPrice(currentHighestBid)}</span>
            <span className="bid-label">Current Highest Bid</span>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onViewDetails(listing) }}
            className="btn-view-docs"
          >
            View Details
          </button>
        </div>
      </div>

      <div className="auction-card-footer">
        <div className="auction-footer-brand">
          <span className="auction-footer-brand-text">SOLDX</span>
        </div>
        <div className="auction-footer-auctions">
          <span className="auction-footer-auctions-title">AUCTIONS</span>
          <span className="auction-footer-auctions-by">
            <span className="fs-italic">by</span> <span className="auction-footer-soldx-tm">SoldX™</span>
          </span>
        </div>
      </div>
    </div>
  )
}

export default AuctionCard
